import React, { useState, useEffect, useRef, useCallback } from "react";
import { apiCallGeneric } from "../../services/api-service";
import { ChevronDown } from "lucide-react";
import { sanitizeInput } from "./security-utils";
import "./Util.css";

const SearchWithFreeText = ({
  onDropdownChange,
  onChangeSearch,
  apiUrl,
  placeholder,
  selectedValue,
  selectedOption,
}) => {
  // State
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [options, setOptions] = useState([]);
  const [filteredOptions, setFilteredOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectIndex, setSelectIndex] = useState(null);

  const dropdownRef = useRef(null);

  // Styles
  const dropdownHeaderStyle = {
    height: "36px",
    padding: "8px",
    border: isOpen ? "1px solid #842234" : "1px solid #E5E5E5",
    borderRadius: "8px",
    cursor: "pointer",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    background: "#FFFFFF",
    color: selectedOption || selectedValue !== "" ? "#1E1E1E" : "#81878C",
  };

  const optionStyle = (option, index) => ({
    padding: "10px",
    cursor: "pointer",
    background:
      selectedOption && selectedOption.id === option.id && index === selectIndex
        ? "#f0f0f0"
        : "#fff",
    color: "#1E1E1E",
    textAlign: "left",
  });

  const dropdownOptionsStyle = {
    position: "absolute",
    top: "100%",
    left: 0,
    right: 0,
    border: "1px solid #ccc",
    borderRadius: "4px",
    background: "#fff",
    zIndex: 1000,
    maxHeight: "200px",
    overflowY: "auto",
    boxShadow: "0 4px 6px rgba(0,0,0,0.1)",
  };

  // Fetch options from API
  const fetchOptions = useCallback(async () => {
    if (!apiUrl) return;

    setLoading(true);
    setError(null);
    try {
      const response = await apiCallGeneric("GET", apiUrl, null, {
        params: { term: searchTerm },
      });

      if (response && response.status === 200 && response.data.success) {
        const list = response.data.suggestionlist || [];
        setOptions(list);
        setFilteredOptions(list);
      } else {
        setOptions([]);
        setFilteredOptions([]);
      }
    } catch (err) {
      // Error is handled by apiCallGeneric/interceptor internally for VAPT
      setError("Failed to fetch options.");
    } finally {
      setLoading(false);
    }
  }, [apiUrl, searchTerm]);

  // Handle outside click
  const handleClickOutside = useCallback((event) => {
    if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
      setIsOpen(false);
    }
  }, []);

  // Toggle dropdown visibility
  const toggleDropdown = () => {
    setIsOpen((prev) => !prev);
  };

  // Handle option selection
  const handleSelectOption = (option, index) => {
    onDropdownChange(option);
    setSelectIndex(index);
    setIsOpen(false);
  };

  // Effects
  useEffect(() => {
    if (selectedValue && typeof selectedValue === "object") {
      setSearchTerm(selectedValue.label || "");
    } else if (typeof selectedValue === "string") {
      setSearchTerm(selectedValue);
    } else {
      setSearchTerm("");
    }
  }, [selectedValue]);

  useEffect(() => {
    if (isOpen) {
      fetchOptions();
    }
  }, [isOpen, fetchOptions]);

  useEffect(() => {
    if (searchTerm === "") {
      setFilteredOptions(options);
    } else {
      if (options.length > 0 && typeof searchTerm === "string") {
        setFilteredOptions(
          options.filter(
            (option) =>
              option.label != null &&
              option.label.toLowerCase().includes(searchTerm.toLowerCase())
          )
        );
      }
    }
  }, [searchTerm, options]);

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [handleClickOutside]);

  // JSX
  return (
    <div ref={dropdownRef} style={{ position: "relative", height: "36px" }}>
      {/* Dropdown Header */}
      <div onClick={toggleDropdown} style={dropdownHeaderStyle}>
        <input
          type="text"
          value={selectedOption ? selectedOption.label : searchTerm}
          autoComplete="off"
          style={{ height: "30px", border: "none", outline: "none", width: "100%" }}
          onChange={(e) => {
            setSearchTerm(e.target.value);
            onChangeSearch(e.target.value);
          }}
          placeholder={placeholder || "Search..."}
          className="dropdown-input"
        />
        <span style={{ marginLeft: "10px" }}>
          <ChevronDown size={20} color="#1E1E1E" />
        </span>
      </div>

      {/* Dropdown Options */}
      {isOpen && (
        <div style={dropdownOptionsStyle}>
          {loading ? (
            <div style={{ padding: "10px", textAlign: "center", fontSize: "13px" }}>
              Loading...
            </div>
          ) : error ? (
            <div style={{ padding: "10px", textAlign: "center", color: "#FF4D4D", fontSize: "13px" }}>
              {sanitizeInput(error)}
            </div>
          ) : filteredOptions.length > 0 ? (
            filteredOptions.map((option, index) => (
              <div
                key={option.id || index}
                onClick={() => handleSelectOption(option, index)}
                style={optionStyle(option, index)}
                className="dropdown-option-item"
              >
                {sanitizeInput(String(option.label || ""))}
              </div>
            ))
          ) : (
            <div style={{ padding: "10px", textAlign: "center", fontSize: "13px", color: "#81878C" }}>
              No options found.
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SearchWithFreeText;
